import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { BookOpen, ChevronRight, Compass, FlaskConical, Landmark, NotebookPen } from 'lucide-react'
import { Button } from './components/ui/button.jsx'
import { LessonCard } from './components/LessonCard.jsx'

// Subject list
const SUBJECTS = [
  { id: 'ela', name: 'ELA', color: 'blue', icon: BookOpen },
  { id: 'math', name: 'Math', color: 'green', icon: NotebookPen },
  { id: 'science', name: 'Science', color: 'purple', icon: FlaskConical },
  { id: 'social-science', name: 'Social Science', color: 'orange', icon: Landmark },
]

// Build a lookup of standards codes -> { subject, gradeLabel }
let _STD_LOOKUP
function getStandardsLookup() {
  if (_STD_LOOKUP) return _STD_LOOKUP
  try {
    const ctx = require.context('./data/standards', false, /\.json$/)
    const map = new Map()
    ctx.keys().forEach((k) => {
      const data = ctx(k)
      const subject = data.subject
      const gradeLabel = data.gradeLabel
      for (const s of data.standards || []) {
        if (s?.code) map.set(s.code, { subject, gradeLabel })
      }
    })
    _STD_LOOKUP = map
  } catch {
    _STD_LOOKUP = new Map()
  }
  return _STD_LOOKUP
}

// Optional alias map: legacyCode -> canonicalCode (if file exists)
let _STD_ALIASES
function getStandardsAliases() {
  if (_STD_ALIASES) return _STD_ALIASES
  try {
    const data = require('./data/standards/aliases.json')
    _STD_ALIASES = new Map(Object.entries(data || {}))
  } catch {
    _STD_ALIASES = new Map()
  }
  return _STD_ALIASES
}

const SUBJECT_COLOR = {
  ela: 'blue',
  math: 'green',
  science: 'purple',
  'social-science': 'orange',
}

function shadeForGrade(gradeLabel) {
  // Map K->200, 1->200, 2->300, 3->300, 4+ -> 400 as simple tiers
  if (!gradeLabel) return 200
  const n = parseInt(String(gradeLabel).replace(/\D/g, ''), 10)
  if (isNaN(n)) return 200
  if (n <= 1) return 200
  if (n <= 3) return 300
  return 400
}

// Extract a likely unit subtitle (e.g., text after the last colon in Skyline titles)
function extractElaUnitSubtitle(title) {
  if (!title || typeof title !== 'string') return null
  const lastColon = title.lastIndexOf(':')
  if (lastColon !== -1 && lastColon < title.length - 1) {
    return title.slice(lastColon + 1).trim()
  }
  const m = title.match(/Unit\s*\d+\s*:\s*(?:Lesson\s*\d+\s*:)?\s*(.+)$/i)
  return m ? m[1].trim() : null
}

// Auto-index ELA lessons from src/data/ela/** via require.context (CRA/Webpack)
function buildElaIndex() {
  let context
  try {
    context = require.context('./data/ela', true, /\.json$/)
  } catch (e) {
    return []
  }

  // Map structure: { 'grade2/unit1': [{ id, title, lessonNum, data }] }
  const buckets = {}
  context.keys().forEach((key) => {
    const data = context(key)
    // key example: './grade2/unit1/g2_ela_u1_l10.json'
    const clean = key.replace(/^\.\//, '')
  const parts = clean.split('/')
    if (parts.length < 3) return
  const grade = parts[0] // e.g., grade2
  const unit = parts[1] // e.g., unit1
    const folderKey = `${grade}/${unit}`

  // derive lesson id/number from filename or JSON
    const filename = parts[parts.length - 1]
    let lessonNum = null
    const matchNum = filename.match(/(?:lesson|_l)(\d+)/i)
    if (matchNum) lessonNum = parseInt(matchNum[1], 10)
    if (!lessonNum && typeof data?.lesson === 'number') lessonNum = data.lesson

    const id = lessonNum ? `lesson${lessonNum}` : filename.replace(/\.json$/, '')
    // For display we want just "Lesson N" to keep Skyline consistent
    const title = lessonNum ? `Lesson ${lessonNum}` : (data?.title || 'Lesson')

    if (!buckets[folderKey]) buckets[folderKey] = []
    buckets[folderKey].push({ id, title, lessonNum: lessonNum ?? 0, data })
  })

  // Build units from buckets
  const units = Object.entries(buckets).map(([folder, lessons]) => {
    const [gradeId, unitId] = folder.split('/')
    const unitNumMatch = unitId.match(/unit(\d+)/i)
    const unitNum = unitNumMatch ? parseInt(unitNumMatch[1], 10) : null
    lessons.sort((a, b) => a.lessonNum - b.lessonNum)
    // Derive a consistent unit subtitle from lesson data titles
    const subtitleCounts = new Map()
    for (const l of lessons) {
      const sub = extractElaUnitSubtitle(l.data?.title)
      if (sub) subtitleCounts.set(sub, (subtitleCounts.get(sub) || 0) + 1)
    }
    let unitSubtitle = null
    if (subtitleCounts.size > 0) {
      unitSubtitle = Array.from(subtitleCounts.entries()).sort((a, b) => b[1] - a[1])[0][0]
    }
    return {
      grade: gradeId,
      id: unitId,
      title: unitSubtitle ? `Unit ${unitNum ?? unitId.replace('unit', '')}: ${unitSubtitle}` : `Unit ${unitNum ?? unitId.replace('unit', '')}`,
      lessons,
    }
  })

  // Sort units by unit number
  units.sort((a, b) => {
    const an = parseInt(a.id.replace(/\D/g, ''), 10)
    const bn = parseInt(b.id.replace(/\D/g, ''), 10)
    return (an || 0) - (bn || 0)
  })

  return units
}

// Auto-index Kindergarten Social Science with custom lesson ordering
function buildSsIndex() {
  let context
  try {
    context = require.context('./data/ss', true, /\.json$/)
  } catch (e) {
    return []
  }

  // Buckets keyed by e.g., 'gradek/unit1'
  const buckets = {}
  context.keys().forEach((key) => {
    const data = context(key)
    const clean = key.replace(/^\.\//, '')
  const parts = clean.split('/')
  if (parts.length < 3) return
  const grade = parts[0] // e.g., gradek
  const unit = parts[1] // e.g., unit1
  const filename = parts[parts.length - 1] // e.g., gk_ss_u1_l1.json
    const folderKey = `${grade}/${unit}`

    // Determine lesson identity and custom sort order
    let lessonId
    let order = 999 // default high to push unknown to end

    // l0 (0), li (0.5), l1..N (1..N), lw (999)
    if (/_l0\.json$/i.test(filename)) {
      lessonId = 'lesson0'
      order = 0
    } else if (/_li\.json$/i.test(filename)) {
      lessonId = 'li'
      order = 0.5
    } else if (/_lw\.json$/i.test(filename)) {
      lessonId = 'lw'
      order = 999
    } else {
      const m = filename.match(/_l(\d+)\.json$/i)
      if (m) {
        const n = parseInt(m[1], 10)
        lessonId = `lesson${n}`
        order = n
      } else if (typeof data?.lesson === 'number') {
        lessonId = `lesson${data.lesson}`
        order = data.lesson
      } else {
        lessonId = filename.replace(/\.json$/, '')
      }
    }

    const title = data?.title || (lessonId.startsWith('lesson') ? `Lesson ${lessonId.replace('lesson', '')}` : lessonId.toUpperCase())

    if (!buckets[folderKey]) buckets[folderKey] = []
    buckets[folderKey].push({ id: lessonId, title, order, data })
  })

  const units = Object.entries(buckets).map(([folder, lessons]) => {
    const [gradeId, unitId] = folder.split('/')
    lessons.sort((a, b) => a.order - b.order)
    // Try to compute a generic title
    const unitNumMatch = unitId.match(/unit(\d+)/i)
    const unitNum = unitNumMatch ? parseInt(unitNumMatch[1], 10) : null
    return {
      grade: gradeId,
      id: unitId,
      title: `Unit ${unitNum ?? unitId.replace('unit', '')}`,
      lessons,
    }
  })

  // Sort by unit number
  units.sort((a, b) => {
    const an = parseInt(a.id.replace(/\D/g, ''), 10)
    const bn = parseInt(b.id.replace(/\D/g, ''), 10)
    return (an || 0) - (bn || 0)
  })

  return units
}

// (guessUnitTitle removed; superseded by extractElaUnitSubtitle)

export function Curriculum() {
  const params = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [subject, setSubject] = useState(null) // 'ela' | 'math' | 'science' | 'social-science'
  const [gradeId, setGradeId] = useState(null) // e.g., 'grade2', 'gradek'
  const [unitId, setUnitId] = useState(null)
  const [lessonId, setLessonId] = useState(null)

  const subjectMeta = useMemo(() => SUBJECTS.find(s => s.id === subject) || null, [subject])

  const elaUnits = useMemo(() => buildElaIndex(), [])
  const ssUnits = useMemo(() => buildSsIndex(), [])
  const units = useMemo(() => {
    let list = []
    if (subject === 'ela') list = elaUnits
    else if (subject === 'social-science') list = ssUnits
    else list = []
    if (gradeId) return list.filter(u => u.grade === gradeId)
    return list
  }, [subject, gradeId, elaUnits, ssUnits])

  const currentUnit = useMemo(() => units.find(u => u.id === unitId) || null, [units, unitId])
  const currentLesson = useMemo(() => currentUnit?.lessons.find(l => l.id === lessonId) || null, [currentUnit, lessonId])

  // Current lessons based on global progress stored in localStorage (from dashboard)
  const currentSuggestions = useMemo(() => {
    const items = []
    let saved = {}
    try { saved = JSON.parse(localStorage.getItem('lp.progress') || '{}') } catch {}
    const entries = Object.values(saved || {})
    entries.forEach(v => {
      const subj = String(v?.subject || '')
      let subjectId = null
      let subjectName = null
      let color = 'blue'
      if (/Skyline\s*ELA/i.test(subj)) { subjectId = 'ela'; subjectName = 'ELA'; color = 'blue' }
      else if (/Social\s*Science/i.test(subj)) { subjectId = 'social-science'; subjectName = 'Social Science'; color = 'orange' }
      if (!subjectId) return
      // Restrict to current subject filter if one is chosen
      if (subject && subject !== subjectId) return

      const grade = v.gradeId
      const unitKey = `unit${v.currentUnitNum}`
      if (subjectId === 'ela') {
        const unit = elaUnits.find(u => u.grade === grade && u.id === unitKey)
        if (!unit) return
        const total = unit.lessons.length
        const lessonN = Math.max(1, Math.min(v.currentLesson || 1, total))
        items.push({
          subjectId, subjectName, color, gradeId: grade, unitId: unit.id,
          lessonId: `lesson${lessonN}`,
          title: `Lesson ${lessonN} of ${total}`,
          description: unit.title,
        })
      } else if (subjectId === 'social-science') {
        const unit = ssUnits.find(u => u.grade === grade && u.id === unitKey)
        if (!unit) return
        const seq = unit.lessons || []
        // Determine current lesson id by phase/introStep
        let id = `lesson${v.currentLesson || 1}`
        if (v.phase === 'intro') {
          id = (v.introStep === 0 && seq.find(l => l.id === 'lesson0')) ? 'lesson0'
            : (seq.find(l => l.id === 'li') ? 'li' : (seq.find(l => l.id === 'lesson0') ? 'lesson0' : id))
        } else if (v.phase === 'wrap') {
          id = seq.find(l => l.id === 'lw') ? 'lw' : id
        }
        const idx = Math.max(0, seq.findIndex(l => l.id === id))
        const displayCount = idx === -1 ? 1 : idx + 1
        const totalSeq = seq.length
        const namePart = v.phase === 'intro' ? (v.introStep === 0 ? 'Lesson 0' : 'Introduction') : (v.phase === 'wrap' ? 'Wrap-Up' : `Lesson ${v.currentLesson}`)
        items.push({
          subjectId, subjectName, color, gradeId: grade, unitId: unit.id,
          lessonId: id,
          title: `${namePart} • Lesson ${displayCount} of ${totalSeq}`,
          description: unit.title,
        })
      }
    })
    return items
  }, [subject, elaUnits, ssUnits])

  const onSelectSuggestion = (s) => {
    setSubject(s.subjectId)
    setGradeId(s.gradeId || null)
    setUnitId(s.unitId)
    setLessonId(s.lessonId)
    if (s.subjectId && s.gradeId && s.unitId && s.lessonId) {
      navigate(`/curriculum/${s.subjectId}/${s.gradeId}/${s.unitId}/${s.lessonId}`)
    }
  }

  const resetTo = (level) => {
    if (level === 'subjects') {
      setSubject(null); setGradeId(null); setUnitId(null); setLessonId(null)
      navigate('/curriculum')
    } else if (level === 'subject') {
      setUnitId(null); setLessonId(null)
    } else if (level === 'unit') {
      setLessonId(null)
    }
  }

  // Sync state from URL (hash-based for HashRouter). Clears missing params to support back/forward correctly.
  useEffect(() => {
    // Prefer params if available (when route is defined with params); otherwise parse hash.
    let { subject: pSubject, grade: pGrade, unit: pUnit, lessonId: pLesson } = params || {}

    const hash = window.location.hash || location.hash || ''
    const parts = hash.replace(/^#\//, '').split('/')
    if (parts[0] === 'curriculum') {
      pSubject = parts[1] ?? pSubject
      pGrade = parts[2] ?? pGrade
      pUnit = parts[3] ?? pUnit
      pLesson = parts[4] ?? pLesson
    }

    // Normalize empty to null so UI updates on back/forward
    setSubject(pSubject || null)
    setGradeId(pGrade || null)
    setUnitId(pUnit || null)
    setLessonId(pLesson || null)
  }, [params, location])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center">
        <Compass className="h-5 w-5 mr-2 text-blue-600" />
        <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Curriculum</h2>
      </div>

      {/* Breadcrumbs */}
      <nav className="text-sm text-gray-600">
        <div className="flex items-center flex-wrap">
          <button
            className="hover:underline"
            onClick={() => {
              resetTo('subjects')
              navigate('/curriculum')
            }}
          >
            Curriculum
          </button>
          {subjectMeta && (
            <>
              <ChevronRight className="h-3.5 w-3.5 mx-2 text-gray-400" />
              <button
                className="hover:underline"
                onClick={() => {
                  resetTo('subject')
                  if (subject) navigate(`/curriculum/${subject}`)
                }}
              >
                {subjectMeta.name}
              </button>
            </>
          )}
          {currentUnit && (
            <>
              <ChevronRight className="h-3.5 w-3.5 mx-2 text-gray-400" />
              <button
                className="hover:underline"
                onClick={() => {
                  resetTo('unit')
                  if (subject && (gradeId || currentUnit?.grade) && currentUnit?.id) {
                    const g = gradeId || currentUnit.grade
                    navigate(`/curriculum/${subject}/${g}/${currentUnit.id}`)
                  }
                }}
              >
                {currentUnit.title}
              </button>
            </>
          )}
          {currentLesson && (
            <>
              <ChevronRight className="h-3.5 w-3.5 mx-2 text-gray-400" />
              <span className="text-gray-800 font-medium">{currentLesson.title}</span>
            </>
          )}
        </div>
      </nav>

      {/* Current lessons */}
      <section className="bg-white rounded-lg border shadow-sm p-4 lg:p-6">
        <div className="flex items-center mb-4">
          <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
          <h3 className="text-lg font-semibold">Current lessons</h3>
        </div>
        {currentSuggestions.length === 0 ? (
          <p className="text-sm text-gray-500">No suggestions yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {currentSuggestions.map((s, i) => (
              <LessonCard
                key={i}
                color={s.color}
                subjectLabel={s.subjectName}
                headline={s.title}
                subline={s.description}
                onView={() => onSelectSuggestion(s)}
                viewLabel="View lesson"
              />
            ))}
          </div>
        )}
      </section>

      {/* Body: subject -> units -> lessons -> details */}
      {!subject && (
        <section className="bg-white rounded-lg border shadow-sm p-4 lg:p-6">
          <h3 className="text-lg font-semibold mb-3">Choose a subject</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {SUBJECTS.map(s => {
              const Icon = s.icon
              return (
                <Button
                  key={s.id}
                  className="justify-start"
                  variant="outline"
                  onClick={() => {
                    setSubject(s.id)
                    setGradeId(null)
                    setUnitId(null)
                    setLessonId(null)
                    navigate(`/curriculum/${s.id}`)
                  }}
                >
                  <Icon className={`h-4 w-4 mr-2 text-${s.color}-600`} />
                  {s.name}
                </Button>
              )
            })}
          </div>
        </section>
      )}

      {subject && !unitId && (
        <section className="bg-white rounded-lg border shadow-sm p-4 lg:p-6">
          <h3 className="text-lg font-semibold mb-3">{subjectMeta?.name} • Units</h3>
          {units.length === 0 ? (
            <p className="text-sm text-gray-500">Coming soon.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {units.map(u => (
                <LessonCard
                  key={u.id}
                  color={SUBJECT_COLOR[subject] || 'blue'}
                  subjectLabel={subjectMeta?.name}
                  headline={u.title}
                  subline={`${u.lessons.length} lesson${u.lessons.length !== 1 ? 's' : ''}`}
                  onView={() => {
                    setUnitId(u.id)
                    setGradeId(u.grade)
                    if (subject && u.grade && u.id) {
                      navigate(`/curriculum/${subject}/${u.grade}/${u.id}`)
                    }
                  }}
                  viewLabel="View lessons"
                />
              ))}
            </div>
          )}
        </section>
      )}

      {subject && unitId && !lessonId && (
        <section className="bg-white rounded-lg border shadow-sm p-4 lg:p-6">
          <h3 className="text-lg font-semibold mb-3">{currentUnit?.title} • Lessons</h3>
          {currentUnit?.lessons?.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {currentUnit.lessons.map(l => (
                <LessonCard
                  key={l.id}
                  color={SUBJECT_COLOR[subject] || 'blue'}
                  subjectLabel={subjectMeta?.name}
                  headline={l.title}
                  onView={() => {
                    setLessonId(l.id)
                    const g = gradeId || currentUnit?.grade
                    const u = unitId || currentUnit?.id
                    if (subject && g && u && l.id) {
                      navigate(`/curriculum/${subject}/${g}/${u}/${l.id}`)
                    }
                  }}
                  viewLabel="Open"
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No lessons found.</p>
          )}
        </section>
      )}

      {subject && unitId && lessonId && currentLesson && (
        <LessonDetail
          data={currentLesson.data}
          displayTitle={`${currentUnit?.title || ''} — Lesson ${currentLesson.lessonNum || currentLesson.id.replace('lesson','')}`}
        />
      )}
    </div>
  )
}

function Section({ title, children }) {
  return (
    <section className="bg-white rounded-lg border shadow-sm p-4 lg:p-6">
      <h3 className="text-lg font-semibold mb-3">{title}</h3>
      {children}
    </section>
  )
}

function renderMdLinks(text) {
  if (!text) return null
  // Render [text](url) links and turn standard codes into in-app chips linking to /standards?code=...
  const parts = []
  const linkRe = /\[(.+?)\]\((https?:\/\/[^)]+)\)/g
  let lastIndex = 0
  let m
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > lastIndex) {
      const chunk = text.slice(lastIndex, m.index)
      // Within plain text chunks, also transform standards codes
      parts.push(...transformStandardsInText(chunk))
    }
    parts.push(
      <a key={`lnk-${parts.length}`} href={m[2]} target="_blank" rel="noreferrer" className="text-blue-600 underline">
        {m[1]}
      </a>
    )
    lastIndex = linkRe.lastIndex
  }
  if (lastIndex < text.length) {
    parts.push(...transformStandardsInText(text.slice(lastIndex)))
  }
  return parts
}

function transformStandardsInText(chunk) {
  const out = []
  // Find dot-separated tokens that could be standards; verify against lookup
  // Support hyphenated grade spans (e.g., K-2, 6-8) and trailing letter parts.
  // Matches patterns like: SS.IS.1.K-2, WHST.6-8.1, RL.2.3, K.MD.1
  const stdRe = /[A-Z0-9](?:[A-Za-z0-9]*)(?:\.(?:[A-Za-z0-9]+(?:-[A-Za-z0-9]+)?))+\b/g
  let idx = 0
  let m
  while ((m = stdRe.exec(chunk)) !== null) {
    if (m.index > idx) out.push(chunk.slice(idx, m.index))
    // Normalize code by stripping trailing punctuation and preserving original for display
    const raw = m[0]
    const code = raw.replace(/[.,;:)]$/,'')
    const lookup = getStandardsLookup()
    let meta = lookup.get(code)
    let linkCode = code
    // If not found, try alias map strictly (no auto-reformat heuristics)
    if (!meta) {
      const alias = getStandardsAliases().get(code)
      if (alias) {
        if (typeof alias === 'string') {
          const m2 = lookup.get(alias)
          if (m2) { meta = m2; linkCode = alias }
        } else if (alias && typeof alias === 'object' && alias.legacy) {
          // Legacy-only code: dark red chip with white text
          out.push(
            <a
              key={`std-${out.length}`}
              href={`#/standards?code=${encodeURIComponent(code)}`}
              title={alias.note || 'Legacy (2017) standard — no modern equivalent'}
              className={`inline-flex items-center px-1 py-0.5 rounded bg-red-600 text-white text-xs font-medium mr-1`}
            >
              {code}
            </a>
          )
          idx = stdRe.lastIndex
          continue
        }
      }
    }
    if (meta) {
      const color = SUBJECT_COLOR[meta.subject] || 'blue'
      const shade = shadeForGrade(meta.gradeLabel)
      // If this chip came from an alias mapping, make it noticeable (light red)
      const isAliasMap = linkCode !== code
      const cls = isAliasMap
        ? 'bg-red-100 text-red-800'
        : `bg-${color}-${shade} text-${color}-800`
      out.push(
        <a
          key={`std-${out.length}`}
          href={`#/standards?code=${encodeURIComponent(linkCode)}`}
          title={isAliasMap ? `${code} maps to ${linkCode}` : undefined}
          className={`inline-flex items-center px-1 py-0.5 rounded ${cls} text-xs font-medium mr-1`}
        >
          {code}
        </a>
      )
    } else {
      out.push(raw)
    }
    idx = stdRe.lastIndex
  }
  if (idx < chunk.length) out.push(chunk.slice(idx))
  return out
}

function ListTree({ items }) {
  if (!items || !items.length) return null
  return (
    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
      {items.map((item, idx) => (
        <li key={idx}>
          <span>{renderMdLinks(item.text || item)}</span>
          {item.children && item.children.length > 0 && (
            <ListTree items={item.children} />
          )}
        </li>
      ))}
    </ul>
  )
}

function LessonDetail({ data, displayTitle }) {
  return (
    <div className="space-y-6">
      <Section title={displayTitle || data.title}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium mb-2">Essential Question</h4>
            {Array.isArray(data.essentialQuestion) ? (
              <ListTree items={data.essentialQuestion} />
            ) : (
              <p className="text-sm text-gray-700">{data.essentialQuestion}</p>
            )}
          </div>
          <div>
            <h4 className="font-medium mb-2">Guiding Question</h4>
            {Array.isArray(data.guidingQuestion) ? (
              <ListTree items={data.guidingQuestion} />
            ) : (
              <p className="text-sm text-gray-700">{data.guidingQuestion}</p>
            )}
          </div>
        </div>
      </Section>

      <Section title="Snapshot">
        <div className="divide-y">
          {data.snapshot?.map((s, idx) => (
            <div key={idx} className="py-3">
              <div className="flex items-center justify-between">
                <h5 className="font-medium text-sm">{s.segment}</h5>
                <span className="text-xs text-gray-500">{s.timing}</span>
              </div>
              <p className="text-sm text-gray-700 mt-1">{s.description}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Learning Objectives">
        {Array.isArray(data.learningObjectives) ? (
          <ListTree items={data.learningObjectives} />
        ) : null}
      </Section>

      <Section title="Standards">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium mb-2">Assessed</h4>
            {Array.isArray(data.standards?.assessed) ? (
              <ListTree items={data.standards.assessed} />
            ) : null}
          </div>
          <div>
            <h4 className="font-medium mb-2">Addressed</h4>
            {Array.isArray(data.standards?.addressed) ? (
              <ListTree items={data.standards.addressed} />
            ) : null}
          </div>
        </div>
      </Section>

      <Section title="Vocabulary">
        {Array.isArray(data.vocabulary) ? (
          <ListTree items={data.vocabulary} />
        ) : (
          <pre className="whitespace-pre-wrap text-sm text-gray-700">{data.vocabulary}</pre>
        )}
      </Section>

      <Section title="Materials">
        {Array.isArray(data.materials) ? (
          <ListTree items={data.materials} />
        ) : (
          <pre className="whitespace-pre-wrap text-sm text-gray-700">{data.materials}</pre>
        )}
      </Section>

      <Section title="Assessments">
        {Array.isArray(data.assessments) ? (
          <ListTree items={data.assessments} />
        ) : (
          <pre className="whitespace-pre-wrap text-sm text-gray-700">{data.assessments}</pre>
        )}
      </Section>
    </div>
  )
}
